import React, { useState, useEffect, useCallback } from "react";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { useDispatch } from "react-redux";
import { addToCart } from "../../reducers/app.reducer";
import {
  bomColumnKeySaveAPI,
  bomDetailAPI,
  createQuoteAPI,
  editPartNumberAPI,
  getBomItemRefreshAPI,
} from "../Home-Page/home-page.component.service";
import BomToolTemplate from "./bom-tool.template";
import { useParams } from "react-router-dom";

const BomToolIndexComponent = ({}) => {
  const dispatch = useDispatch();
  const MySwal = withReactContent(Swal);
  const [bomData, setBomData] = useState([]);
  const [partNumberKey, setPartNumberKey] = useState("");
  const [qtyKey, setQtyKey] = useState("");
  // const [filter, setFilter] = useState("all");
  const [filteredData, setFilteredData] = useState(bomData);
  const [activeFilters, setActiveFilters] = useState(["all"]);
  const [editingPartNumber, setEditingPartNumber] = useState({});
  const [bomDetail, setBomDetail] = useState([]);
  const params = useParams();
  const uniqueId = params?.id;
  const [selectedItems, setSelectedItems] = useState({});
  const [selectAll, setSelectAll] = useState(false);

  // console.log({uniqueId});

  const onLoad = async () => {
    const response = await bomDetailAPI(uniqueId);
    console.log({ response });
    if (response.status) {
      setBomDetail(response.data);
      setBomData(response.data);
      setFilteredData(response.data);
    }
  };

  useEffect(() => {
    onLoad();
  }, []);

  const handleSelectItem = (itemId, isSelected) => {
    setSelectedItems((prev) => ({
      ...prev,
      [itemId]: isSelected,
    }));
  };

  const handleSelectAll = (isSelected) => {
    const newSelected = {};
    if (isSelected) {
      filteredData.forEach((item) => {
        // Only select items that can be added to cart
        if (item.inventory_match || item.alternative_match) {
          newSelected[item.id] = true;
        }
      });
    }
    setSelectedItems(newSelected);
    setSelectAll(isSelected && Object.keys(newSelected).length > 0);
  };

  const handleAddSelectedToCart = () => {
    const selectedIds = Object.keys(selectedItems).filter(
      (id) => selectedItems[id],
    );
    if (selectedIds.length === 0) {
      MySwal.fire({
        title: <strong>No Selection</strong>,
        html: <i>Please select at least one item to add to cart</i>,
        icon: "warning",
      });
      return;
    }

    // Get selected items that have either inventory_match or alternative_match
    const validSelectedItems = filteredData.filter(
      (item) =>
        selectedItems[item.id] &&
        (item.inventory_match || item.alternative_match),
    );

    if (validSelectedItems.length === 0) {
      MySwal.fire({
        title: <strong>No Valid Items</strong>,
        html: (
          <i>
            Selected items must have inventory or alternative matches to be
            added to cart
          </i>
        ),
        icon: "warning",
      });
      return;
    }

    // Add each valid item to cart
    validSelectedItems.forEach((item) => {
      handleAddToCart(item);
    });

    // Show success message
    MySwal.fire({
      title: <strong>Success</strong>,
      html: <i>Added {validSelectedItems.length} items to cart</i>,
      icon: "success",
    });
  };

  const handleFilterChange = (type, e) => {
    setActiveFilters((prev) => {
      const isMultiSelect = e.ctrlKey || e.metaKey;

      // Clicking "all" always resets
      if (type === "all") {
        return ["all"];
      }

      // SINGLE click → only one filter
      if (!isMultiSelect) {
        return [type];
      }

      // MULTI select
      let filters = prev.filter((f) => f !== "all");

      if (filters.includes(type)) {
        filters = filters.filter((f) => f !== type);
      } else {
        filters.push(type);
      }

      return filters.length ? filters : ["all"];
    });
  };

  useEffect(() => {
    if (activeFilters.includes("all")) {
      setFilteredData(bomData);
      return;
    }

    const filtered = bomData.filter((row) => {
      // console.log({row});

      return activeFilters.some((type) => {
        // console.log({activeFilters});

        if (type === "inStock") {
          return (
            row.inventory_match &&
            row.inventory_match.available_qty >= (row.required_qty || row.qty)
          );
        }

        if (type === "noStock") {
          return !row.inventory_match && !row.alternative_match;
        }

        if (type === "partialCount") {
          return (
            row.inventory_match &&
            row.inventory_match.available_qty > 0 &&
            row.inventory_match.available_qty < (row.required_qty || row.qty)
          );
        }

        if (type === "alternative") {
          return !row.inventory_match && row.alternative_match;
        }

        return false;
      });
    });

    setFilteredData(filtered);
  }, [activeFilters, bomData]);

  const handleAddToCart = (row) => {
    console.log({ row });

    const match = row.inventory_match || row.alternative_match;
    if (!match) {
      MySwal.fire({
        title: <strong>No Match!</strong>,
        html: <i>{row.part_number} is not found in inventory</i>,
        icon: "error",
      });
      return;
    }

    const matchedQty = row.qty || 1;
    const availableQty = match.available_qty ?? 0;
    const partnerAvailableQty = match.partner_available_qty ?? 0;
    const totalAvailableQty = availableQty + partnerAvailableQty;

    // Check if requested quantity exceeds total available stock (immediate + partner)
    if (matchedQty > totalAvailableQty) {
      MySwal.fire({
        title: <strong>Insufficient Stock!</strong>,
        html: (
          <i>
            Only {totalAvailableQty} available (Immediate: {availableQty},
            Partner: {partnerAvailableQty}) for {row.part_number}, but you
            requested {matchedQty}.
          </i>
        ),
        icon: "warning",
      });
      return;
    }

    // Check if requested quantity is greater than immediate stock - show alert and proceed
    const isPartnerDelivery = matchedQty > availableQty;
    if (isPartnerDelivery) {
      MySwal.fire({
        title: <strong>Partner Delivery Alert!</strong>,
        html: (
          <i>
            Your need ({matchedQty}) is greater than our immediate stock (
            {availableQty}) for {row.part_number}, so we will deliver through
            our partner. Your need is deliver in 10 days.
          </i>
        ),
        icon: "info",
      });
    }

    // Proceed with adding to cart
    proceedWithAddToCart(row, match, matchedQty, isPartnerDelivery);
  };

  // Helper function to handle the actual add to cart logic
  const proceedWithAddToCart = (row, match, matchedQty, isPartnerDelivery) => {
    let priceList = [];
    if (Array.isArray(match.price)) {
      const sortedPrices = [...match.price].sort((a, b) => {
        const aQty = parseInt(a.slab.replace("slab_", ""));
        const bQty = parseInt(b.slab.replace("slab_", ""));
        return aQty - bQty;
      });

      priceList = sortedPrices.map((p) => ({
        min_qty: parseInt(p.slab.replace("slab_", "")),
        price: parseFloat(p.price),
      }));
    }

    if (matchedQty < 10) {
      MySwal.fire({
        title: <strong>Low Quantity!</strong>,
        html: <i>Quantity for {row.part_number} is less than 10</i>,
        icon: "warning",
      });
      return;
    }

    const cartProduct = {
      ...match,
      required_qty: matchedQty,
      price: {
        min_order: matchedQty,
        multiple: matchedQty,
        price_list: priceList,
      },
      manufacturer: match.make,
      is_partner_delivery: isPartnerDelivery, // Add flag to indicate partner delivery
    };

    console.log({ cartProduct });

    const successMessage = isPartnerDelivery
      ? `You've added ${row.part_number} (Qty: ${matchedQty}) to your cart. This will be delivered through our partner Within 10 days.`
      : `You've added ${row.part_number} (Qty: ${matchedQty}) to your cart`;

    MySwal.fire({
      title: <strong>Cart Update!</strong>,
      html: <i>{successMessage}</i>,
      icon: "success",
    });

    dispatch(addToCart(cartProduct));
  };

  const getMatchedPrice = (prices, qty) => {
    const sortedPrices = [...prices].sort(
      (a, b) =>
        parseInt(a.slab.replace("slab_", "")) -
        parseInt(b.slab.replace("slab_", "")),
    );

    const matchedPrice =
      sortedPrices.find((p) => parseInt(p.slab.replace("slab_", "")) >= qty) ||
      sortedPrices[sortedPrices.length - 1];

    return `₹${matchedPrice.price}`;
  };
  const getExtendedPrice = (prices, qty) => {
    if (!qty || isNaN(qty)) return "–";
    const sortedPrices = [...prices].sort(
      (a, b) =>
        parseInt(a.slab.replace("slab_", "")) -
        parseInt(b.slab.replace("slab_", "")),
    );

    const matchedPrice =
      sortedPrices.find((p) => parseInt(p.slab.replace("slab_", "")) >= qty) ||
      sortedPrices[sortedPrices.length - 1];

    if (!matchedPrice?.price) return "–";
    const total = qty * parseFloat(matchedPrice.price);
    return `₹${total.toFixed(2)}`;
  };

  // useEffect(() => {
  //     const rawResult = sessionStorage.getItem("bom_result");
  //     if (rawResult) {
  //       try {
  //         const parsed = JSON.parse(rawResult);
  //         console.log({ parsed });
  //         setBomData(parsed);
  //         setFilteredData(parsed); // default to "all"
  //       } catch (e) {
  //         console.error("Failed to parse bom_result:", e);
  //       }
  //     }

  //     const partKey = sessionStorage.getItem("partNumberKey");
  //     const qtyKey = sessionStorage.getItem("qtyKey");

  //     if (partKey && qtyKey) {
  //       setPartNumberKey(partKey);
  //       setQtyKey(qtyKey);
  //     }
  //   }, []);

  const handleExportBomTable = async () => {
    let filteredData = bomData;

    if (activeFilters.includes("inStock")) {
      filteredData = bomData.filter(
        (row) => row.inventory_match && row.inventory_match.available_qty > 0,
      );
    } else if (activeFilters.includes("noStock")) {
      filteredData = bomData.filter(
        (row) => !row.inventory_match || row.inventory_match.available_qty <= 0,
      );
    } else if (activeFilters.includes("partialCount")) {
      filteredData = bomData.filter(
        (row) =>
          row.inventory_match &&
          row.qty > row.inventory_match.available_qty &&
          row.inventory_match.available_qty > 0,
      );
    } else if (activeFilters.includes("alternative")) {
      filteredData = bomData.filter((row) => row.alternative_match);
    }

    let exportList = [];
    filteredData.forEach((row, index) => {
      const hasExactMatch = !!row.inventory_match;
      const hasAlternative = !!row.alternative_match;
      const match = hasExactMatch
        ? row.inventory_match
        : hasAlternative
          ? row.alternative_match
          : null;

      if (!match) {
        exportList.push({
          sno: index + 1,
          part_number: row.part_number,
          qty: row.qty,
          part_match: "No Match",
          quoted_mpn: "-",
          manufacturer: "-",
          description: "No matching inventory found",
          availability: "-",
          min_mul: "-",
          suggested_qty: "-",
          lead_time: "-",
          unit_price: "-",
          ext_price: "-",
        });
        return;
      }

      const minQty = parseInt(match.min_qty || 10, 10);
      const mulQty = parseInt(match.mul_qty || 10, 10);
      const requestedQty = parseInt(row.qty || 10, 10);

      let supplyQty = Math.ceil(requestedQty / mulQty) * mulQty;
      if (supplyQty < minQty) supplyQty = minQty;

      const unitPrice =
        match?.price?.length > 0
          ? getMatchedPrice(match.price, supplyQty)
          : "No price";
      const extPrice =
        match?.price?.length > 0
          ? getExtendedPrice(match.price, supplyQty)
          : "No price";

      exportList.push({
        sno: index + 1,
        part_number: row.part_number,
        qty: row.qty,
        part_match: hasExactMatch ? "Exact" : "Alternative",
        quoted_mpn: match?.part_number || "-",
        manufacturer: match?.make || "-",
        description: match?.description || "-",
        availability: match?.available_qty ?? "-",
        min_mul: `${minQty} / ${mulQty}`,
        suggested_qty: supplyQty,
        lead_time: match?.lead_time || "-",
        unit_price: unitPrice,
        ext_price: extPrice,
      });
    });

    const response = await createQuoteAPI({ exportList });

    if (response?.status) {
      const fileLocation = response.filePath;
      const fileName = response.fileName || "bom-table-export.xlsx";

      const a = document.createElement("a");
      a.href = fileLocation;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(fileLocation);
    } else {
      alert(response?.message || "Export failed");
    }
  };

  const handlePartNumberCancel = (itemId, item) => {
    // Cancel editing and revert to original value
    setEditingPartNumber((prev) => {
      const updated = { ...prev };
      delete updated[itemId];
      return updated;
    });
  };

  const handlePartNumberChange = (
    itemId,
    newPartNumber,
    originalPartNumber,
  ) => {
    if (newPartNumber !== originalPartNumber) {
      setEditingPartNumber((prev) => ({
        ...prev,
        [itemId]: newPartNumber,
      }));
    } else {
      // If reverted to original, remove from editing state
      setEditingPartNumber((prev) => {
        const updated = { ...prev };
        delete updated[itemId];
        return updated;
      });
    }
  };

  const handlePartNumberSubmit = async (item) => {
    console.log("part number submit");

    const newPartNumber = editingPartNumber[item.id];
    console.log({ newPartNumber });

    if (
      newPartNumber &&
      newPartNumber.trim() !== "" &&
      newPartNumber !== item.part_number
    ) {
      console.log("condition chexk");

      try {
        console.log("checkkk");

        const response = await editPartNumberAPI(item.id, {
          part_number: newPartNumber.trim(),
        });
        console.log({ response });
        if (response.status) {
          setBomDetail((prev) =>
            prev.map((row) =>
              row.id === item.id
                ? { ...row, part_number: newPartNumber.trim() }
                : row,
            ),
          );
          onLoad();
        } else {
        }
      } catch (error) {
        console.error("Error updating part number:", error);
      }
    }

    // Clear editing state after submit
    setEditingPartNumber((prev) => {
      const updated = { ...prev };
      delete updated[item.id];
      return updated;
    });
  };

  const handlePartNumberKeyDown = async (e, item) => {
    const hasChanges =
      editingPartNumber[item.id] !== undefined &&
      editingPartNumber[item.id] !== item.part_number;

    if (e.key === "Enter" && hasChanges) {
      e.preventDefault();
      await handlePartNumberSubmit(item);
    } else if (e.key === "Escape" && hasChanges) {
      e.preventDefault();
      handlePartNumberCancel(item.id, item);
    }
  };
  // console.log({ bomDetail });

  const templateModal = {
    bomData: filteredData,
    totalParts: bomData.length,
    inStockCount: bomData.filter(
      (r) =>
        r.inventory_match &&
        r.inventory_match.available_qty >= (r.required_qty || r.qty),
    ).length,
    partialCount: bomData.filter(
      (r) =>
        r.inventory_match &&
        r.inventory_match.available_qty < (r.required_qty || r.qty),
    ).length,
    noStockCount: bomData.filter(
      (r) => !r.inventory_match && !r.alternative_match,
    ).length,
    alternativeCount: bomData.filter(
      (r) => !r.inventory_match && r.alternative_match,
    ).length,
    // activeFilter: filter,
    filteredData,
    activeFilters,
    // activeFilter,
    editingPartNumber,
    bomDetail,
    selectAll,
    selectedItems,
  };

  const templateEvents = {
    handleAddToCart,
    getMatchedPrice,
    getExtendedPrice,
    handleFilterChange,
    handleExportBomTable,
    handlePartNumberChange,
    handlePartNumberKeyDown,
    handlePartNumberSubmit,
    handlePartNumberCancel,
    handleAddSelectedToCart,
    handleSelectItem,
    handleSelectAll,
  };
  return (
    <>
      <BomToolTemplate
        templateModal={templateModal}
        templateEvents={templateEvents}
      />
    </>
  );
};
export default BomToolIndexComponent;
